import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mercadoPagoApi, getAuthSession } from "@/services";
import { toast } from "sonner";
import { useTenant } from "./useTenant";
import { useAllIntegrationsStatus } from "./useIntegrationsStatus";
import type { CartItem } from "@/types/checkout";

interface MercadoPagoStatus {
  connected: boolean;
  user?: {
    email: string;
    nickname: string;
  };
}

interface ConnectCredentials {
  access_token: string;
  public_key: string;
}

export function useMercadoPagoStatus() {
  const { data: integrations, isLoading } = useAllIntegrationsStatus();

  const data = useMemo((): MercadoPagoStatus => {
    const integration = integrations?.find(
      (i) => i.provider === "mercadopago" && i.is_active
    );
    if (!integration) return { connected: false };
    const metadata = integration.metadata as Record<string, unknown> | null;
    return {
      connected: true,
      user: (metadata?.user as MercadoPagoStatus["user"]) || undefined,
    };
  }, [integrations]);

  return { data, isLoading };
}

export function useConnectMercadoPago() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (credentials: ConnectCredentials) => {
      await getAuthSession();

      const response = await mercadoPagoApi.connect(credentials);

      if (response.error) throw response.error;
      if (response.data.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
      toast.success("Mercado Pago conectado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao conectar Mercado Pago");
    },
  });
}

export function useDisconnectMercadoPago() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async () => {
      await getAuthSession();

      const response = await mercadoPagoApi.disconnect();

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
      toast.success("Mercado Pago desconectado");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao desconectar");
    },
  });
}

async function extractErrorMessage(error: unknown): Promise<string> {
  if (error && typeof error === "object" && "context" in error) {
    try {
      const response = (error as { context: Response }).context;
      if (response && typeof response.json === "function") {
        const body = await response.json();
        if (body?.error) return body.error;
      }
    } catch {
      // ignore parse failure
    }
  }
  if (error instanceof Error) return error.message;
  return "Erro ao criar checkout";
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (data: { items: CartItem[]; subtotal: number }) => {
      const response = await mercadoPagoApi.createSession(data);

      if (response.error) {
        const msg = await extractErrorMessage(response.error);
        throw new Error(msg);
      }
      if (response.data.error) throw new Error(response.data.error);

      return response.data;
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao criar checkout");
    },
  });
}

export function useCreateCheckoutForSale() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (data: { saleId: string; items: CartItem[]; subtotal: number; discount?: number }) => {
      const response = await mercadoPagoApi.createSessionForSale(data);

      if (response.error) {
        const msg = await extractErrorMessage(response.error);
        throw new Error(msg);
      }
      if (response.data.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["customer-sales"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao criar checkout");
    },
  });
}
